import { cloneDeep } from 'lodash'
import {
  coords3dTo2d,
  getCorrectRotation,
  xmlString,
  xmlUrlString
} from '~js/helpers'
import getMarkerGalleryXml from './marker-gallery'
import getMarkerMemoXml from './marker-memo'
import getMarkerNarrationXml from './marker-narration'
import getMarkerPointXml from './marker-point'
import getMarkerPointBuildingXml from './marker-point-building'
import getMarkerPopupXml from './marker-popup'
import getMarkerTagXml from './marker-tag'
import getMarkerTeleportXml from './marker-teleport'
import getMarkerVRXml from './marker-vr'
import getMarkerVideoXml from './marker-video'
import getMarkerMultitagXml from './marker-multitag'
import getMarkerStickerXml from './marker-sticker'
import getMarkerSearchXml from './marker-search'
import getMarkerPdfXml from './marker-pdf'
import server from '~js/server'
const urlStart = server.firebaseStorageUrlStart || ''

const getHighResolutionCubemapUrl = path => {
  return urlStart + path.replace(/panoramas(\/|%2F)/, 'panoramas/cubemap_%s_%l_%h_%v_')
}
const getScenesXml = (panoramas, startIndex, krpanoXOffset, customSetting, krpanoVrModeObj, nextPanoramaCategoryName, defaultFov) => {
  let scene = ''
  const clonedPanoramas = cloneDeep(panoramas)
  const startUpPanorama = clonedPanoramas.splice(startIndex, 1)
  clonedPanoramas.unshift(...startUpPanorama)
  clonedPanoramas.forEach((panorama) => {
    let hotspot = ''
    if (panorama.markers && panorama.markers.length) {
      panorama.markers.forEach((marker, index) => {
        if (marker.hidden) {
          return
        }
        let { ath, atv } = coords3dTo2d(marker.position)
        ath = getCorrectRotation(ath + krpanoXOffset)
        const useCustomIcon = customSetting.customBranding && marker.useCustomIcon && marker.iconUrl
        const tagsizePercent = marker.tagsizePercent || 100
        const markerScale = tagsizePercent / 100
        const category = nextPanoramaCategoryName(marker)
        switch (marker.type) {
          case 'point':
            if (marker.nextBuildingId) {
              hotspot += getMarkerPointBuildingXml(marker, ath, atv, useCustomIcon, index, markerScale)
            } else {
              const isMarkerPoint = true
              hotspot += getMarkerPointXml(marker, ath, atv, category, useCustomIcon, index, markerScale, isMarkerPoint, krpanoXOffset)
            }
            break
          case 'teleport':
            if (marker.nextBuildingId && marker.nextPanoramaId) {
              hotspot += getMarkerTeleportXml(marker, ath, atv, useCustomIcon, index, markerScale)
            }
            break
          case 'memo':
            hotspot += getMarkerMemoXml(marker, ath, atv, useCustomIcon, index, markerScale)
            break
          case 'tag':
            hotspot += getMarkerTagXml(marker, ath, atv, useCustomIcon, index, markerScale, krpanoVrModeObj)
            break
          case 'gallery':
            hotspot += getMarkerGalleryXml(marker, ath, atv, useCustomIcon, index, markerScale, krpanoVrModeObj)
            break
          case 'popup':
          case 'arMakerTag':
            // krpanoVrModeObj.vrModeShouldHide.push(`marker_${marker.objectId}`)
            hotspot += getMarkerPopupXml(marker, ath, atv, useCustomIcon, index, markerScale)
            break
          case 'pdf':
            hotspot += getMarkerPdfXml(marker, ath, atv, useCustomIcon, index, markerScale)
            break
          case 'narration':
            hotspot += getMarkerNarrationXml(marker, ath, atv, category, useCustomIcon, index, markerScale)
            break
          case 'videoplay':
            hotspot += getMarkerVideoXml({ marker, ath, atv, category, useCustomIcon, index, markerScale, krpanoXOffset })
            break
          case 'multitag':
            hotspot += getMarkerMultitagXml(marker, ath, atv, category, useCustomIcon, index, markerScale)
            break
          case 'sticker':
            hotspot += getMarkerStickerXml(marker, ath, atv, category, useCustomIcon, index, markerScale)
            break
          case 'search':
            hotspot += getMarkerSearchXml(marker, ath, atv, category, useCustomIcon, index, markerScale)
            break
          default:
        }
      })
      hotspot += getMarkerVRXml(krpanoVrModeObj)
    }
    scene += getSceneXml(panorama, hotspot, defaultFov, krpanoXOffset)
  })
  return scene
}

const getSceneXml = function (panorama, hotspot, defaultFov, krpanoXOffset) {
  const previewXml = panorama.cubemapPreivewUrl
    ? `<preview url="${xmlUrlString(panorama.cubemapPreivewUrl)}" />`
    : `<preview url="${xmlUrlString(panorama.previewUrl)}" />`
  const sphereXml = `
    <image>
      <sphere url="${xmlUrlString(panorama.adjustedRawUrl || panorama.desktopUrl || '')}" />
    </image>
  `
  let cubemapXml = ''
  if (panorama.cubemapFilePath && panorama.cubemapLevel > 0) {
    const highResolutionCubemapUrl = getHighResolutionCubemapUrl(panorama.cubemapFilePath)
    cubemapXml = `<image type='CUBE' multires="true" tilesize="512">
                    ${Array.from({ length: panorama.cubemapLevel }).map((_, i) => i + 1).map((level) => {
                        return `<level tiledimagewidth="${512 * Math.pow(2, level - 1)}" tiledimageheight="${512 * Math.pow(2, level - 1)}">
                                  <cube url="${highResolutionCubemapUrl.replace(/%l/g, level)}" />
                                </level>`
                    }).join('')}
                  </image>`
  } else {
    cubemapXml = `<image><cube url="${panorama.cubemapUrl ? xmlUrlString(panorama.cubemapUrl) : ''}" /></image>`
  }
  const sceneXml = `
    <scene name="panorama_${panorama.objectId}" title="${xmlString(panorama.name)}" objectId="${panorama.objectId}" compassDegree="${panorama.compassDegree}" isTopLogo="${panorama.isTopLogo}">
      <view hlookat="${(panorama.panoramaRotation ? -panorama.panoramaRotation.y : 0) + krpanoXOffset}" vlookat="0" fovtype="MFOV" fov="${defaultFov}" fovmin="30" fovmax="${defaultFov}"
      limitview="fullrange" vlookatmin="-90" vlookatmax="90" />
      ${previewXml}
      ${(panorama.cubemapReady && !panorama.adjustedRawUrl) ? cubemapXml : sphereXml}
      ${hotspot}
    </scene>`
  return sceneXml
}

export default getScenesXml
